import { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch } from "react-icons/fi";

export interface ComboCard {
  _id: string;
  name: string;
  rate: number | string;
  type?: string;
  isDisabled?: boolean;
}

interface Props {
  id?: string;
  cards: ComboCard[];
  /** Selected card id, "0" or "" when nothing is chosen. */
  value: string;
  onChange: (card: ComboCard) => void;
  onBlur?: () => void;
  placeholder?: string;
}

/**
 * Select2-style card picker (task sheet Oct T12). The field is a button: a click
 * anywhere on it opens a dropdown whose first row is a search box (focused), and
 * the list below filters as you type. Arrow keys move, Enter picks, Escape or a
 * click outside closes. Enter never submits the surrounding trade form.
 * The caller passes only the eligible cards (right trade type, enabled).
 */
export function CardCombobox({ id = "card", cards, value, onChange, onBlur, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = cards.find((c) => c._id === value) ?? null;
  const q = query.trim().toLowerCase();
  const matches = q ? cards.filter((c) => c.name.toLowerCase().includes(q)) : cards;

  // `picked` = closing because a card was chosen: the caller sets the value and
  // touched state together, so no separate blur validation (it would run
  // against the stale "no card" value and flash "You must select a card").
  const close = (picked = false) => {
    setOpen(false);
    setQuery("");
    if (!picked) onBlur?.();
  };

  const pick = (card: ComboCard) => {
    onChange(card);
    close(true);
  };

  // Focus the search box and highlight the current selection on open.
  useEffect(() => {
    if (!open) return;
    const i = selected ? matches.findIndex((c) => c._id === selected._id) : 0;
    setActive(i >= 0 ? i : 0);
    requestAnimationFrame(() => searchRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the highlight to the first match whenever the query changes.
  useEffect(() => setActive(0), [query]);

  // Close on a click outside the component.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault(); // never submit the trade form
      if (matches[active]) pick(matches[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      close();
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`relative w-full text-left bg-gray-600 border rounded-lg px-4 py-3 pr-10 text-sm transition-all duration-200 focus:outline-none ${
          open ? "border-primary" : "border-primary/40"
        } ${selected ? "text-white/90" : "text-white/40"}`}
      >
        <span className="block truncate">{selected?.name ?? placeholder ?? "Select a card"}</span>
        <FiChevronDown
          className={`absolute top-1/2 right-3 -translate-y-1/2 text-white/60 text-base transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg bg-gray-700 border border-primary/40 shadow-lg">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Search cards…"
                autoComplete="off"
                aria-label="Search cards"
                className="w-full bg-gray-600 rounded-md pl-9 pr-3 py-2 text-sm text-white/90 placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          {matches.length === 0 ? (
            <div className="px-4 py-3 text-sm text-white/50">
              {q ? `No cards match “${query.trim()}”` : "No cards available"}
            </div>
          ) : (
            <ul ref={listRef} role="listbox" className="max-h-60 overflow-auto py-1 text-sm">
              {matches.map((card, i) => {
                const isSelected = card._id === selected?._id;
                return (
                  <li
                    key={card._id}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(card)}
                    className={`relative cursor-pointer select-none py-2 pl-9 pr-4 ${
                      i === active ? "bg-primary/30 text-white" : "text-white/80"
                    }`}
                  >
                    <span className={`block truncate ${isSelected ? "font-semibold text-white" : ""}`}>{card.name}</span>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                        <FiCheck />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
